import React, { useState, useRef } from 'react';
import { FaShieldAlt } from 'react-icons/fa';
import api from '../api';
import { theme, t } from '../theme';
import { TERMOS_USO, POLITICA_PRIVACIDADE, TERMOS_VERSAO, PRIVACIDADE_VERSAO, ULTIMA_ATUALIZACAO } from './consentContent';

/**
 * Aceite dos Termos de Uso e da Política de Privacidade — um passo só.
 *
 * ANTES eram dois modais em sequência, com dois cliques. Ninguém lia o
 * segundo: depois de aceitar o primeiro, o seguinte vira obstáculo, não
 * informação. Um passo com os dois textos disponíveis lado a lado dá o
 * mesmo acesso e pede uma decisão em vez de duas.
 *
 * O REGISTRO CONTINUA SEPARADO. `/consent/accept` recebe os dois de uma vez,
 * mas o banco guarda data e versão de cada documento. É o que permite subir
 * a política sem forçar novo aceite dos termos — e, se alguém perguntar o que
 * exatamente foi aceito, a resposta existe por documento.
 */
export default function ConsentModal({ onComplete }) {
  const [aba, setAba] = useState('termos'); // qual texto está visível
  const [aceitou, setAceitou] = useState(false);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState(null);
  const scrollRef = useRef(null);

  const trocarAba = (nova) => {
    setAba(nova);
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  };

  const aceitar = async () => {
    setLoading(true);
    setErro(null);
    try {
      await api.post('/consent/accept', { termos: true, privacidade: true });
      onComplete();
    } catch (e) {
      setErro('Não consegui registrar o aceite. Verifique a conexão e tente de novo.');
    } finally {
      setLoading(false);
    }
  };

  const ehTermos = aba === 'termos';
  const conteudo = ehTermos ? TERMOS_USO : POLITICA_PRIVACIDADE;
  const versao = ehTermos ? TERMOS_VERSAO : PRIVACIDADE_VERSAO;

  const estiloAba = (ativa) => ({
    flex: 1,
    padding: '10px 12px',
    fontSize: 13.5,
    fontWeight: ativa ? 700 : 500,
    color: ativa ? theme.colors.primary : theme.colors.textSecondary,
    background: 'transparent',
    border: 'none',
    borderBottom: `2px solid ${ativa ? theme.colors.primary : 'transparent'}`,
    cursor: 'pointer',
  });

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(61,44,44,0.55)',
      zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 16, backdropFilter: 'blur(4px)',
    }}>
      <div style={{
        background: theme.colors.bgCard, borderRadius: 20, maxWidth: 560, width: '100%',
        maxHeight: '92vh', display: 'flex', flexDirection: 'column',
        boxShadow: theme.shadow.xl, overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{ padding: '20px 24px 0', borderBottom: `1px solid ${theme.colors.border}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10,
              background: theme.colors.primaryLight,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <FaShieldAlt style={{ color: theme.colors.primary, fontSize: 16 }} />
            </div>
            <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: theme.colors.text, letterSpacing: 0.3 }}>
              TERMOS E PRIVACIDADE
            </h2>
          </div>
          <p style={{ margin: '0 0 14px', fontSize: 13, color: theme.colors.textSecondary }}>
            Atualizamos a Política de Privacidade. Leia e aceite para continuar.
          </p>

          {/* Abas: os dois textos, um aceite */}
          <div style={{ display: 'flex', gap: 4 }}>
            <button type="button" onClick={() => trocarAba('termos')} style={estiloAba(ehTermos)}>
              Termos de Uso
            </button>
            <button type="button" onClick={() => trocarAba('privacidade')} style={estiloAba(!ehTermos)}>
              Política de Privacidade
            </button>
          </div>
        </div>

        {/* Conteúdo scrollável */}
        <div ref={scrollRef} style={{
          flex: 1, overflowY: 'auto', padding: '20px 24px',
          background: theme.colors.bgSection,
        }}>
          <div style={{
            background: theme.colors.bgCard, borderRadius: 12,
            padding: '18px 20px', border: `1px solid ${theme.colors.border}`,
            fontSize: 14, lineHeight: 1.7, color: theme.colors.text,
          }}>
            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>Metria — Assistente para Confeiteiras</div>
            <div style={{ color: theme.colors.textMuted, fontSize: 12, marginBottom: 16 }}>
              Última atualização: {ULTIMA_ATUALIZACAO} • Versão {versao}
            </div>
            <div style={{ whiteSpace: 'pre-wrap', fontSize: 13.5 }}>
              {conteudo}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: '16px 24px 20px', borderTop: `1px solid ${theme.colors.border}`, background: theme.colors.bgCard }}>
          {erro && <div style={{ ...t.error, marginBottom: 10 }}>{erro}</div>}
          <label style={{
            display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer',
            padding: '10px 0', userSelect: 'none',
          }}>
            <input
              type="checkbox" checked={aceitou}
              onChange={e => setAceitou(e.target.checked)}
              style={{ width: 18, height: 18, accentColor: theme.colors.primary, cursor: 'pointer', marginTop: 2, flexShrink: 0 }}
            />
            <span style={{ fontSize: 14, color: theme.colors.text, fontWeight: 500, lineHeight: 1.5 }}>
              Li e aceito os <strong>Termos de Uso</strong> e a <strong>Política de Privacidade</strong>
            </span>
          </label>
          <button
            onClick={aceitar} disabled={!aceitou || loading}
            style={{
              ...t.btnPrimary, width: '100%', padding: '14px', fontSize: 15, fontWeight: 600,
              marginTop: 8, opacity: (!aceitou || loading) ? 0.5 : 1,
              cursor: (!aceitou || loading) ? 'not-allowed' : 'pointer',
            }}
          >
            {loading ? 'Salvando...' : 'Aceitar e continuar'}
          </button>
        </div>
      </div>
    </div>
  );
}
